import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { mediaAPI } from '../lib/api';
import { Film, ImageIcon, Folder, FolderOpen, Download, Clock, User, FileVideo, Play, X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, RotateCw } from 'lucide-react';
import { Button } from '../components/ui/button';

const formatSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

const formatDate = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const SharedMediaPage = () => {
  const { shareToken } = useParams();
  const [media, setMedia] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewingPhoto, setViewingPhoto] = useState(null);
  const [playingVideo, setPlayingVideo] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await mediaAPI.sharedInfo(shareToken);
        setMedia(res.data);
      } catch (e) {
        setError('Midia nao encontrada ou link expirado');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [shareToken]);

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="w-8 h-8 border-3 border-gold/30 border-t-gold rounded-full animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <Film size={48} className="text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">{error}</p>
      </div>
    </div>
  );

  if (!media) return null;

  const streamUrl = mediaAPI.sharedStreamUrl(shareToken);

  // Single Photo view
  if (media.media_type === 'photo') return (
    <div className="min-h-screen bg-gray-950 flex flex-col" data-testid="shared-photo-page">
      <div className="bg-gray-900 border-b border-gray-800 px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-white font-medium text-sm">{media.filename}</h1>
            <p className="text-gray-400 text-xs">{media.project_name} | {media.uploaded_by} | {formatDate(media.created_at)}</p>
          </div>
          <a href={streamUrl} download className="text-gray-400 hover:text-green-400"><Download size={18} /></a>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-4">
        <img src={streamUrl} alt={media.filename} className="max-w-full max-h-[85vh] object-contain" />
      </div>
    </div>
  );

  // Single Video view
  if (media.media_type === 'video') return (
    <div className="min-h-screen bg-gray-950 flex flex-col" data-testid="shared-video-page">
      <div className="bg-gray-900 border-b border-gray-800 px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-white font-medium text-sm">{media.filename}</h1>
            <p className="text-gray-400 text-xs">{media.project_name} | {media.uploaded_by} | {formatDate(media.created_at)}</p>
          </div>
          <a href={streamUrl} download className="text-gray-400 hover:text-green-400"><Download size={18} /></a>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-4">
        <video src={streamUrl} controls autoPlay className="max-w-full max-h-[85vh]" data-testid="shared-video-player" />
      </div>
    </div>
  );

  // Folder view
  if (media.media_type === 'folder') {
    const contents = media.contents || [];
    const photos = contents.filter(c => c.media_type === 'photo');
    const videos = contents.filter(c => c.media_type === 'video');

    return (
      <div className="min-h-screen bg-gray-50" data-testid="shared-folder-page">
        <div className="bg-white border-b border-gray-200 px-4 py-4">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-3">
              <FolderOpen size={24} className="text-gold" />
              <div>
                <h1 className="text-navy font-semibold text-lg">{media.name}</h1>
                <p className="text-gray-400 text-xs">{media.project_name} | {contents.length} itens | {media.uploaded_by}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="max-w-5xl mx-auto p-4 space-y-4">
          {photos.length > 0 && (
            <div>
              <h3 className="text-xs font-mono text-gray-400 uppercase mb-2 flex items-center gap-1"><ImageIcon size={12} /> FOTOS ({photos.length})</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {photos.map(p => (
                  <div key={p.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden cursor-pointer hover:shadow-md transition-shadow" onClick={() => setViewingPhoto(p)}>
                    <div className="aspect-square bg-gray-100">
                      <img src={`/api/shared-media/${p.share_token}/stream`} alt={p.original_filename} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <div className="p-2">
                      <p className="text-navy text-xs truncate">{p.original_filename}</p>
                      <p className="text-gray-400 text-[9px]">{formatSize(p.size)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {videos.length > 0 && (
            <div>
              <h3 className="text-xs font-mono text-gray-400 uppercase mb-2 flex items-center gap-1"><Film size={12} /> VIDEOS ({videos.length})</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {videos.map(v => (
                  <div key={v.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden cursor-pointer hover:shadow-md transition-shadow" onClick={() => setPlayingVideo(v)}>
                    <div className="aspect-video bg-gray-900 flex items-center justify-center">
                      <Play size={32} className="text-white/60" />
                    </div>
                    <div className="p-2">
                      <p className="text-navy text-xs truncate">{v.original_filename}</p>
                      <p className="text-gray-400 text-[9px]">{formatSize(v.size)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {contents.length === 0 && (
            <div className="text-center py-12">
              <Folder size={40} className="text-gray-300 mx-auto mb-2" />
              <p className="text-gray-400 text-sm">Pasta vazia</p>
            </div>
          )}
        </div>

        {/* Photo lightbox */}
        {viewingPhoto && (
          <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center" data-testid="shared-lightbox">
            <button onClick={() => setViewingPhoto(null)} className="absolute top-4 right-4 text-white/60 hover:text-white"><X size={24} /></button>
            <img src={`/api/shared-media/${viewingPhoto.share_token}/stream`} alt={viewingPhoto.original_filename} className="max-w-full max-h-[90vh] object-contain" style={{ transform: `scale(${zoom}) rotate(${rotation}deg)` }} />
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 bg-black/60 rounded-full px-3 py-1.5">
              <button onClick={() => setZoom(z => Math.min(z + 0.25, 4))} className="text-white/60 hover:text-white"><ZoomIn size={16} /></button>
              <button onClick={() => setZoom(z => Math.max(z - 0.25, 0.25))} className="text-white/60 hover:text-white"><ZoomOut size={16} /></button>
              <button onClick={() => setRotation(r => r + 90)} className="text-white/60 hover:text-white"><RotateCw size={16} /></button>
            </div>
          </div>
        )}

        {/* Video player */}
        {playingVideo && (
          <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center" data-testid="shared-video-modal">
            <button onClick={() => setPlayingVideo(null)} className="absolute top-4 right-4 text-white/60 hover:text-white"><X size={24} /></button>
            <video src={`/api/shared-media/${playingVideo.share_token}/stream`} controls autoPlay className="max-w-full max-h-[90vh]" />
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default SharedMediaPage;
